import { FC, useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Stack,
} from '@mui/material';
import {
  pieArcClasses,
  PieChart,
  pieClasses,
  BarChart,
  ChartsTooltip,
  ChartsTooltipProps,
  chartsTooltipClasses,
  continuousColorLegendClasses,
  piecewiseColorLegendClasses,
  ContinuousColorLegend,
} from '@mui/x-charts';
import { useGetDashboardQuery } from '../../api/controllers/admin-dashboard';
import { Table } from '../../components/table/table';
import { Row, RowCell } from '../../components/table/types';
import { formatAmountWithCurrencyToString, formatFiatAmountWithCurrency } from '../../utils';
import { useTranslation } from 'react-i18next';

export const Dashboard: FC = () => {
  const { t } = useTranslation();
  const { data: dashboardData } = useGetDashboardQuery();

  const allCoins =
    dashboardData?.wallets_total.map((item) => ({
      id: `${item.blockchain}-${item.coin_code}`,
      label: `${item.coin_code} (${item.blockchain})`,
    })) ?? [];

  const [selectedCoins, setSelectedCoins] = useState<string[]>(allCoins.map((c) => c.id));
  const [walletChartType, setWalletChartType] = useState<'pie' | 'bar' | 'table'>('pie');

  useEffect(() => {
    if (allCoins.length > 0 && selectedCoins.length === 0) {
      setSelectedCoins(allCoins.map((c) => c.id));
    }
  }, [dashboardData]);

  const handleCoinsChange = (event: any) => {
    const value = event.target.value as string[];
    setSelectedCoins(value);
  };

  const TOTAL_WALLETS =
    dashboardData?.wallets_total.reduce((acc, item) => acc + Number(item.total), 0) ?? 0;

  const getArcPieWalletLabel = (params: number) => {
    const percent = params / TOTAL_WALLETS;
    return `${percent * 100 > 0.00009 ? (percent * 100).toFixed(4) : 0}%`;
  };

  const filteredWallets =
    dashboardData?.wallets_total
      .filter((item) => selectedCoins.includes(`${item.blockchain}-${item.coin_code}`))
      .map((item) => ({
        value: Number(item.total),
        label: `${item.coin_code} (${item.blockchain})`,
      })) ?? [];

  const walletLabels = filteredWallets.map((f) => f.label);
  const walletValues = filteredWallets.map((f) => f.value);

  const [selectedCoin, setSelectedCoin] = useState<string>('ALL');
  const [transactionsByCoinTypeValue, setTransactionsByCoinTypeValue] = useState<
    'TURNOVER' | 'COUNT'
  >('TURNOVER');
  const coins = dashboardData?.transactions_popularity.map((item) => item.coin_code) ?? [];
  const coinOptions = ['ALL', ...Array.from(new Set(coins))];

  const [txTableMode, setTxTableMode] = useState<'TOTAL' | 'IN' | 'OUT'>('TOTAL');
  const [volumeTableMode, setVolumeTableMode] = useState<'TOTAL' | 'IN' | 'OUT'>('TOTAL');

  const walletTableRows: Row[] = filteredWallets
    ? [...filteredWallets]
        .sort((a, b) => Number(b.value) - Number(a.value))
        .map((item) => {
          const cells: RowCell[] = [
            { name: 'coin', value: item.label },
            { name: 'balance', value: formatAmountWithCurrencyToString(item.value, '€') },
          ];
          return {
            cells,
            onClick: () => {
              if (coinOptions.includes(item.label.split(' ')[0]))
                setSelectedCoin(item.label.split(' ')[0]);
            },
          };
        })
    : [];

  const txRows: Row[] = dashboardData?.transactions_popularity
    ? [...dashboardData.transactions_popularity]
        .sort((a, b) => {
          if (txTableMode === 'TOTAL') return Number(b.count) - Number(a.count);
          if (txTableMode === 'IN') return Number(b.in_count) - Number(a.in_count);
          return Number(b.out_count) - Number(a.out_count);
        })
        .slice(0, 5)
        .map((item) => {
          const cells: RowCell[] = [
            { name: 'coin', value: `${item.coin_code} (${item.blockchain})` },
            {
              name: txTableMode.toLowerCase(),
              value:
                txTableMode === 'TOTAL'
                  ? item.count
                  : txTableMode === 'IN'
                    ? item.in_count
                    : item.out_count,
            },
          ];
          return { cells };
        })
    : [];

  const volumeRows: Row[] = dashboardData?.transactions_popularity
    ? [...dashboardData.transactions_popularity]
        .sort((a, b) => {
          if (volumeTableMode === 'TOTAL') return Number(b.turnover) - Number(a.turnover);
          if (volumeTableMode === 'IN') return Number(b.in_turnover) - Number(a.in_turnover);
          return Number(b.out_turnover) - Number(a.out_turnover);
        })
        .slice(0, 5)
        .map((item) => {
          const cells: RowCell[] = [
            { name: 'coin', value: `${item.coin_code} (${item.blockchain})` },
            {
              name: volumeTableMode.toLowerCase(),
              value: formatFiatAmountWithCurrency(
                volumeTableMode === 'TOTAL'
                  ? item.turnover
                  : volumeTableMode === 'IN'
                    ? item.in_turnover
                    : item.out_turnover,
                '€'
              ),
            },
          ];
          return { cells };
        })
    : [];

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            height: walletChartType === 'pie' ? '100%' : 600,
            maxHeight: walletChartType === 'pie' ? '100%' : 600,
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {t('dashboard.wallet-balance')}
          </Typography>
          <Stack direction={'row'} justifyContent={'space-between'} gap={2}>
            <Select
              multiple
              value={selectedCoins}
              onChange={handleCoinsChange}
              variant="standard"
              fullWidth
              sx={{ maxWidth: 300 }}
            >
              {allCoins.map((coin) => (
                <MenuItem key={coin.id} value={coin.id}>
                  {coin.label}
                </MenuItem>
              ))}
            </Select>

            <ToggleButtonGroup
              value={walletChartType}
              exclusive
              onChange={(e, v) => v && setWalletChartType(v)}
            >
              <ToggleButton value="pie">{t('common.chart-type.pie')}</ToggleButton>
              <ToggleButton value="bar">{t('common.chart-type.bar')}</ToggleButton>
              <ToggleButton value="table">{t('common.chart-type.table')}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {walletChartType === 'pie' && (
            <PieChart
              series={[
                {
                  data: filteredWallets,
                  highlightScope: { fade: 'global', highlight: 'item' },
                  valueFormatter: (v) =>
                    v != null
                      ? `${formatAmountWithCurrencyToString(v.value, '€')} (${getArcPieWalletLabel(v.value)})`
                      : '',
                },
              ]}
              width={300}
              height={300}
              sx={{
                [`.${pieClasses.series}[data-series="outer"] .${pieArcClasses.root}`]: {
                  opacity: 0.6,
                },
              }}
              onItemClick={(_, item) => {
                if (item && 'dataIndex' in item) {
                  const clickedData = filteredWallets[item.dataIndex];
                  if (coinOptions.includes(clickedData.label.split(' ')[0]))
                    setSelectedCoin(clickedData.label.split(' ')[0]);
                }
              }}
              slotProps={{
                legend: {
                  sx: {
                    overflowY: 'auto',
                    flexWrap: 'nowrap',
                    maxHeight: '500px',
                  },
                },
              }}
            />
          )}

          {walletChartType === 'bar' && (
            <BarChart
              xAxis={[{ scaleType: 'band', data: walletLabels }]}
              series={[
                {
                  data: walletValues,
                  label: 'Total',
                  valueFormatter: (v) =>
                    v != null
                      ? `${formatAmountWithCurrencyToString(v, '€')} (${getArcPieWalletLabel(v)})`
                      : '',
                },
              ]}
              width={500}
              height={400}
            />
          )}

          {walletChartType === 'table' && <Table rows={walletTableRows} />}
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Paper sx={{ p: 2, display: 'flex', height: 600, flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('dashboard.transaction-by-coin')}
          </Typography>
          <Stack direction={'row'} justifyContent={'space-between'} gap={2}>
            <Select
              value={selectedCoin}
              onChange={(e) => setSelectedCoin(e.target.value)}
              variant="standard"
              sx={{ maxWidth: 200, width: '100%' }}
            >
              {coinOptions.map((coin) => (
                <MenuItem key={coin} value={coin}>
                  {coin}
                </MenuItem>
              ))}
            </Select>

            <ToggleButtonGroup
              value={transactionsByCoinTypeValue}
              exclusive
              onChange={(e, v) => v && setTransactionsByCoinTypeValue(v)}
              size="small"
            >
              <ToggleButton value="COUNT">{t('dashboard.count')}</ToggleButton>
              <ToggleButton value="TURNOVER">{t('dashboard.volume')}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {selectedCoin === 'ALL' ? (
            <BarChart
              xAxis={[
                {
                  scaleType: 'band',
                  data:
                    dashboardData?.transactions_popularity.map(
                      (tx) => `${tx.coin_code} (${tx.blockchain})`
                    ) ?? [],
                },
              ]}
              series={[
                {
                  label: 'IN',
                  stack: 'io',
                  color: '#FFB422',
                  data:
                    dashboardData?.transactions_popularity.map((tx) =>
                      transactionsByCoinTypeValue === 'TURNOVER'
                        ? Number(tx.in_turnover)
                        : Number(tx.in_count)
                    ) ?? [],
                  // @ts-ignore
                  valueFormatter: (v, ctx) => {
                    const coinData = dashboardData?.transactions_popularity[ctx.dataIndex];
                    if (!coinData || transactionsByCoinTypeValue === 'COUNT') return `${v}`;
                    return formatFiatAmountWithCurrency(v, '€');
                  },
                },
                {
                  label: 'OUT',
                  stack: 'io',
                  color: '#FA4F58',
                  data:
                    dashboardData?.transactions_popularity.map((tx) =>
                      transactionsByCoinTypeValue === 'TURNOVER'
                        ? Number(tx.out_turnover)
                        : Number(tx.out_count)
                    ) ?? [],
                  // @ts-ignore
                  valueFormatter: (v, ctx) => {
                    const coinData = dashboardData?.transactions_popularity[ctx.dataIndex];
                    if (!coinData) return `${v}`;
                    return (
                      <div
                        style={{ display: 'flex', flexDirection: 'column', marginBottom: '-40px' }}
                      >
                        <div style={{ marginBottom: '8px' }}>
                          {transactionsByCoinTypeValue === 'COUNT'
                            ? v
                            : formatFiatAmountWithCurrency(v, '€')}
                        </div>
                        <span
                          style={{
                            marginLeft: '-87px',
                            borderTop: '1px solid #000',
                            paddingTop: '8px',
                          }}
                        >
                          <span>{t('dashboard.total-label')}</span>{' '}
                          {transactionsByCoinTypeValue === 'COUNT'
                            ? coinData.count
                            : formatFiatAmountWithCurrency(coinData.turnover, '€')}
                        </span>
                      </div>
                    );
                  },
                },
              ]}
              width={500}
              height={400}
              slotProps={{
                tooltip: {
                  sx: {
                    [`&.${chartsTooltipClasses.root} .${chartsTooltipClasses.paper}`]: {
                      height: '140px',
                    },
                    [`&.${chartsTooltipClasses.root} .${chartsTooltipClasses.table}`]: {
                      paddingBottom: '40px',
                    },
                  },
                },
              }}
            />
          ) : (
            (() => {
              const coinData = dashboardData?.transactions_popularity.find(
                (tx) => tx.coin_code === selectedCoin
              );
              if (!coinData) return null;
              return (
                <BarChart
                  xAxis={[{ scaleType: 'band', data: ['COUNT', 'IN', 'OUT'] }]}
                  series={[
                    {
                      data: [
                        transactionsByCoinTypeValue === 'TURNOVER'
                          ? Number(coinData.turnover)
                          : Number(coinData.count),
                        null,
                        null,
                      ],
                      color: '#4254FB',
                      label: 'COUNT',
                      valueFormatter: (v) =>
                        v != null
                          ? `${transactionsByCoinTypeValue === 'TURNOVER' ? formatAmountWithCurrencyToString(v, '€') : v}`
                          : '',
                    },
                    {
                      data: [
                        null,
                        transactionsByCoinTypeValue === 'TURNOVER'
                          ? Number(coinData.in_turnover)
                          : Number(coinData.in_count),
                        null,
                      ],
                      color: '#FFB422',
                      label: 'IN',
                      valueFormatter: (v) =>
                        v != null
                          ? `${transactionsByCoinTypeValue === 'TURNOVER' ? formatAmountWithCurrencyToString(v, '€') : v}`
                          : '',
                    },
                    {
                      data: [
                        null,
                        null,
                        transactionsByCoinTypeValue === 'TURNOVER'
                          ? Number(coinData.out_turnover)
                          : Number(coinData.out_count),
                      ],
                      color: '#FA4F58',
                      label: 'OUT',
                      valueFormatter: (v) =>
                        v != null
                          ? `${transactionsByCoinTypeValue === 'TURNOVER' ? formatAmountWithCurrencyToString(v, '€') : v}`
                          : '',
                    },
                  ]}
                  width={500}
                  height={300}
                />
              );
            })()
          )}
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('dashboard.top-coins-by-transactions')}
          </Typography>
          <ToggleButtonGroup
            value={txTableMode}
            exclusive
            onChange={(e, v) => v && setTxTableMode(v)}
            size="small"
          >
            <ToggleButton value="TOTAL">{t('dashboard.total')}</ToggleButton>
            <ToggleButton value="IN">{t('dashboard.in')}</ToggleButton>
            <ToggleButton value="OUT">{t('dashboard.out')}</ToggleButton>
          </ToggleButtonGroup>
          <Table rows={txRows} />
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('dashboard.top-coins-by-volume')}
          </Typography>
          <ToggleButtonGroup
            value={volumeTableMode}
            exclusive
            onChange={(e, v) => v && setVolumeTableMode(v)}
            size="small"
          >
            <ToggleButton value="TOTAL">{t('dashboard.total')}</ToggleButton>
            <ToggleButton value="IN">{t('dashboard.in')}</ToggleButton>
            <ToggleButton value="OUT">{t('dashboard.out')}</ToggleButton>
          </ToggleButtonGroup>
          <Table rows={volumeRows} />
        </Paper>
      </Grid>
    </Grid>
  );
};
